package com.siriusxm.cmc.utils

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.collectAsState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.coroutines.CoroutineContext
import kotlin.coroutines.EmptyCoroutineContext

/**
 * A state flow that triggers on the first load.
 */
public class LazyLoadedState<T>(
    public val loader: suspend () -> T,
) {
    private val flow = MutableStateFlow<T?>(null)
    private val stateFlow = flow.asStateFlow()
    private val loadMutex = Mutex()
    private var loaded: Boolean = false

    @Composable
    public fun collect(context: CoroutineContext = EmptyCoroutineContext): State<T?> {
        LaunchedEffect(Unit) {
            loadMutex.withLock {
                if (!loaded) {
                    flow.value = loader()
                    loaded = true
                }
            }
        }
        return stateFlow.collectAsState(context)
    }

    public fun value(): T? = flow.value

    /**
     * Update the value.
     */
    public fun update(value: T) {
        flow.value = value
    }

    /**
     * Invokes the loader.
     */
    public suspend fun reload() {
        loadMutex.withLock {
            flow.value = loader()
            loaded = true
        }
    }
}

/**
 * A state flow that can be triggered imperatively (not requiring a composable).
 * Unlike LazyLoadedState, loading can be started via startLoading() method,
 * making it suitable for eager/background initialization on app startup.
 *
 * @param scope The coroutine scope to use for background loading
 * @param loader The suspend function that loads the data
 */
public class EagerLoadedState<T>(
    private val scope: CoroutineScope,
    public val loader: suspend () -> T,
) {
    private val flow = MutableStateFlow<T?>(null)
    private val stateFlow = flow.asStateFlow()
    private var loadingJob: Job? = null
    private var loaded: Boolean = false

    /**
     * Error that occurred during loading, if any.
     */
    public var error: Throwable? = null
        private set

    /**
     * Whether loading is currently in progress.
     */
    public val isLoading: Boolean get() = loadingJob?.isActive == true

    /**
     * Start loading in the background. Safe to call multiple times -
     * subsequent calls are no-ops if already loading or loaded.
     */
    public fun startLoading() {
        if (loaded || loadingJob?.isActive == true) return
        loadingJob = scope.launch {
            try {
                flow.value = loader()
                loaded = true
                error = null
            } catch (e: Throwable) {
                error = e
                console.error("EagerLoadedState loading failed: ${e.message}")
            }
        }
    }

    /**
     * Collect the state in a composable. Will start loading if not already started.
     */
    @Composable
    public fun collect(context: CoroutineContext = EmptyCoroutineContext): State<T?> {
        LaunchedEffect(Unit) {
            if (!loaded && loadingJob?.isActive != true) {
                startLoading()
            }
        }
        return stateFlow.collectAsState(context)
    }

    /**
     * Get the current value (may be null if not loaded yet).
     */
    public fun value(): T? = flow.value

    /**
     * Update the value directly.
     */
    public fun update(value: T) {
        flow.value = value
    }

    /**
     * Reload the data. This is a suspend function that waits for completion.
     */
    public suspend fun reload() {
        error = null
        flow.value = loader()
    }
}